import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getLeads, createLead, updateLead, deleteLead, addLeadActivity, deleteLeadActivity } from '../api/client'
import { currentLocale } from '../i18n'

const STAGE_DEFS = [
  { id: 'new',       color: 'bg-gray-100 text-gray-700',        dot: 'bg-gray-400' },
  { id: 'contacted', color: 'bg-blue-100 text-blue-700',        dot: 'bg-blue-400' },
  { id: 'qualified', color: 'bg-violet-100 text-violet-700',    dot: 'bg-violet-500' },
  { id: 'proposal',  color: 'bg-amber-100 text-amber-700',      dot: 'bg-amber-500' },
  { id: 'won',       color: 'bg-emerald-100 text-emerald-700',  dot: 'bg-emerald-500' },
  { id: 'lost',      color: 'bg-red-100 text-red-600',          dot: 'bg-red-400' },
]

const ACTIVITY_TYPE_DEFS = [
  { id: 'note',    icon: '📝' },
  { id: 'call',    icon: '📞' },
  { id: 'email',   icon: '✉️' },
  { id: 'meeting', icon: '🤝' },
]

const CURRENCIES = ['USD', 'CLP', 'EUR']

function fmtValue(value, currency) {
  if (value == null) return null
  return `${Number(value).toLocaleString(currentLocale(), { maximumFractionDigits: 0 })} ${currency}`
}
function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
}
function fmtDateFull(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' })
}
function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

const EMPTY_LEAD = {
  company_name: '', contact_name: '', contact_email: '', contact_phone: '',
  stage: 'new', value: '', currency: 'USD', notes: '',
}

export default function CRM() {
  const { t } = useTranslation(['crm', 'common'])

  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [form, setForm] = useState(EMPTY_LEAD)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [actForm, setActForm] = useState({ type: 'note', summary: '', activity_date: todayISO() })
  const [actSaving, setActSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try { setLeads(await getLeads()) } catch { setLeads([]) }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const selectedLead = leads.find(l => l.id === selected) || null

  const submitNew = async () => {
    if (!form.company_name.trim()) { setFormError(t('pipeline.companyRequired')); return }
    setSaving(true); setFormError('')
    try {
      const created = await createLead({
        ...form,
        value: form.value === '' ? null : Number(form.value),
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
      })
      setLeads(ls => [created, ...ls])
      setShowNew(false)
      setSelected(created.id)
      setEditMode(false)
    } catch (e) { setFormError(e.response?.data?.detail || t('pipeline.createFailed')) }
    setSaving(false)
  }

  const moveStage = async (lead, newStage) => {
    try {
      const updated = await updateLead(lead.id, { stage: newStage })
      setLeads(ls => ls.map(l => l.id === updated.id ? updated : l))
    } catch {}
  }

  const togglePending = async (lead) => {
    try {
      const updated = await updateLead(lead.id, { is_pending: !lead.is_pending })
      setLeads(ls => ls.map(l => l.id === updated.id ? updated : l))
    } catch {}
  }

  const saveEdit = async () => {
    try {
      const updated = await updateLead(selected, {
        ...editForm,
        value: editForm.value === '' || editForm.value == null ? null : Number(editForm.value),
        contact_name: editForm.contact_name || null,
        contact_email: editForm.contact_email || null,
        contact_phone: editForm.contact_phone || null,
        notes: editForm.notes || null,
      })
      setLeads(ls => ls.map(l => l.id === updated.id ? updated : l))
      setEditMode(false)
    } catch {}
  }

  const handleDelete = async () => {
    if (!window.confirm(t('pipeline.deleteConfirm'))) return
    try {
      await deleteLead(selected)
      setLeads(ls => ls.filter(l => l.id !== selected))
      setSelected(null); setEditMode(false)
    } catch {}
  }

  const submitActivity = async () => {
    if (!actForm.summary.trim()) return
    setActSaving(true)
    try {
      const updated = await addLeadActivity(selected, actForm)
      setLeads(ls => ls.map(l => l.id === updated.id ? updated : l))
      setActForm(f => ({ ...f, summary: '' }))
    } catch {}
    setActSaving(false)
  }

  const handleDeleteActivity = async (actId) => {
    try {
      await deleteLeadActivity(actId)
      setLeads(ls => ls.map(l => ({ ...l, activities: l.activities.filter(a => a.id !== actId) })))
    } catch {}
  }

  const pipelineLeads = leads.filter(l => !['won', 'lost'].includes(l.stage) && l.value != null)
  const wonLeads = leads.filter(l => l.stage === 'won' && l.value != null)

  if (loading) return <div className="p-8 text-sm text-gray-400">{t('common:actions.loading')}</div>

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="px-8 py-5 border-b border-gray-100 bg-white flex items-center justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-[26px] font-bold text-gray-900 tracking-tight leading-tight">{t('pipeline.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('pipeline.leadCount', { count: leads.length })}</p>
        </div>
        <div className="flex items-center gap-6">
          {pipelineLeads.length > 0 && (
            <div className="text-right">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">{t('pipeline.statPipeline')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {pipelineLeads.reduce((s, l) => s + l.value, 0).toLocaleString(currentLocale(), { maximumFractionDigits: 0 })} USD
              </p>
            </div>
          )}
          {wonLeads.length > 0 && (
            <div className="text-right">
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">{t('pipeline.statWon')}</p>
              <p className="text-sm font-semibold text-emerald-600">
                {wonLeads.reduce((s, l) => s + l.value, 0).toLocaleString(currentLocale(), { maximumFractionDigits: 0 })} USD
              </p>
            </div>
          )}
          <button
            onClick={() => { setForm(EMPTY_LEAD); setFormError(''); setShowNew(true) }}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors shrink-0"
          >
            {t('pipeline.newLeadBtn')}
          </button>
        </div>
      </div>

      {/* Kanban board */}
      <div className={`flex-1 overflow-x-auto ${selected ? 'pr-[400px]' : ''}`}>
        <div className="flex gap-3 p-6 h-full" style={{ minWidth: selected ? '700px' : '900px' }}>
          {STAGE_DEFS.map(stage => {
            const colLeads = leads.filter(l => l.stage === stage.id)
            return (
              <div key={stage.id} className="flex flex-col flex-1 min-w-[160px]">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className={`w-2 h-2 rounded-full ${stage.dot} shrink-0`} />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider truncate">{t(`stages.${stage.id}`)}</span>
                  <span className="ml-auto text-xs text-gray-400 shrink-0">{colLeads.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {colLeads.map(lead => (
                    <div
                      key={lead.id}
                      onClick={() => { setSelected(lead.id); setEditMode(false) }}
                      className={`relative bg-white border rounded-xl p-3 cursor-pointer hover:shadow-sm transition-all ${
                        selected === lead.id ? 'border-gray-900 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {lead.is_pending && (
                        <span
                          title={t('pipeline.pending')}
                          className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white"
                        />
                      )}
                      <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2 pr-4">{lead.company_name}</p>
                      {lead.contact_name && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{lead.contact_name}</p>}
                      {lead.value != null && <p className="text-[11px] font-medium text-gray-600 mt-1.5 truncate">{fmtValue(lead.value, lead.currency)}</p>}
                      <p className="text-[10px] text-gray-300 mt-1.5">{fmtDate(lead.updated_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Detail panel */}
      {selectedLead && (
        <div className="fixed inset-y-0 right-0 w-[400px] bg-white border-l border-gray-200 shadow-xl flex flex-col z-30 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3 shrink-0">
            <div className="flex-1 min-w-0">
              {editMode ? (
                <input
                  className="w-full text-lg font-bold text-gray-900 border-b border-gray-300 pb-0.5 focus:outline-none focus:border-gray-700 bg-transparent"
                  value={editForm.company_name || ''}
                  onChange={e => setEditForm(f => ({ ...f, company_name: e.target.value }))}
                />
              ) : (
                <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">{selectedLead.company_name}</h2>
              )}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {editMode ? (
                  <select className="text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none"
                    value={editForm.stage || 'new'} onChange={e => setEditForm(f => ({ ...f, stage: e.target.value }))}>
                    {STAGE_DEFS.map(s => <option key={s.id} value={s.id}>{t(`stages.${s.id}`)}</option>)}
                  </select>
                ) : (
                  <>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STAGE_DEFS.find(s => s.id === selectedLead.stage)?.color || ''}`}>
                      {t(`stages.${selectedLead.stage}`)}
                    </span>
                    {(() => {
                      const idx = STAGE_DEFS.findIndex(s => s.id === selectedLead.stage)
                      return (
                        <div className="flex gap-1">
                          {idx > 0 && (
                            <button onClick={() => moveStage(selectedLead, STAGE_DEFS[idx - 1].id)}
                              className="text-[10px] text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded border border-gray-200 hover:border-gray-400 transition-colors">
                              ← {t(`stages.${STAGE_DEFS[idx - 1].id}`)}
                            </button>
                          )}
                          {idx < STAGE_DEFS.length - 1 && (
                            <button onClick={() => moveStage(selectedLead, STAGE_DEFS[idx + 1].id)}
                              className="text-[10px] text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded border border-gray-200 hover:border-gray-400 transition-colors">
                              {t(`stages.${STAGE_DEFS[idx + 1].id}`)} →
                            </button>
                          )}
                        </div>
                      )
                    })()}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {editMode ? (
                <>
                  <button onClick={saveEdit} className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors">{t('common:actions.save')}</button>
                  <button onClick={() => setEditMode(false)} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors">{t('common:actions.cancel')}</button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => togglePending(selectedLead)}
                    title={selectedLead.is_pending ? t('pipeline.clearPendingTitle') : t('pipeline.markPendingTitle')}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1.5 ${
                      selectedLead.is_pending
                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${selectedLead.is_pending ? 'bg-red-500' : 'bg-gray-400'}`} />
                    {selectedLead.is_pending ? t('pipeline.pending') : t('pipeline.markPending')}
                  </button>
                  <button onClick={() => { setEditForm({ ...selectedLead, value: selectedLead.value ?? '' }); setEditMode(true) }}
                    className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors">{t('common:actions.edit')}</button>
                  <button onClick={handleDelete} className="text-xs text-red-500 hover:text-red-700 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors">{t('common:actions.delete')}</button>
                </>
              )}
              <button onClick={() => { setSelected(null); setEditMode(false) }}
                className="text-gray-400 hover:text-gray-700 ml-1 hover:bg-gray-100 rounded-lg p-1.5 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'contact_name',  label: t('pipeline.editContactName'),  span: 2, type: 'text' },
                    { key: 'contact_email', label: t('pipeline.editContactEmail'), span: 2, type: 'email' },
                    { key: 'contact_phone', label: t('pipeline.editContactPhone'), span: 2, type: 'text' },
                  ].map(({ key, label, span, type }) => (
                    <div key={key} className={`col-span-${span}`}>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">{label}</label>
                      <input type={type} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                        value={editForm[key] || ''} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('pipeline.editDealValue')}</label>
                    <input type="number" min="0" className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                      value={editForm.value ?? ''} onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('pipeline.editCurrency')}</label>
                    <select className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                      value={editForm.currency || 'USD'} onChange={e => setEditForm(f => ({ ...f, currency: e.target.value }))}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('pipeline.editNotes')}</label>
                    <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                      value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedLead.contact_name  && <InfoRow label={t('pipeline.labelContact')} value={selectedLead.contact_name} />}
                {selectedLead.contact_email && (
                  <div className="flex gap-2 text-sm">
                    <span className="text-gray-400 w-20 shrink-0 text-xs pt-0.5">{t('pipeline.labelEmail')}</span>
                    <a href={`mailto:${selectedLead.contact_email}`} className="text-blue-600 hover:underline truncate">{selectedLead.contact_email}</a>
                  </div>
                )}
                {selectedLead.contact_phone && <InfoRow label={t('pipeline.labelPhone')} value={selectedLead.contact_phone} />}
                {selectedLead.value != null  && <InfoRow label={t('pipeline.labelValue')} value={<span className="font-semibold">{fmtValue(selectedLead.value, selectedLead.currency)}</span>} />}
                {selectedLead.owner         && <InfoRow label={t('pipeline.labelOwner')} value={selectedLead.owner.display_name} />}
                {selectedLead.notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-xl text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedLead.notes}</div>
                )}
              </div>
            )}

            {!editMode && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('pipeline.activityLog')}</p>
                <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-2">
                  <div className="flex gap-2">
                    <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none bg-white"
                      value={actForm.type} onChange={e => setActForm(f => ({ ...f, type: e.target.value }))}>
                      {ACTIVITY_TYPE_DEFS.map(typ => <option key={typ.id} value={typ.id}>{typ.icon} {t(`activityTypes.${typ.id}`)}</option>)}
                    </select>
                    <input type="date" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none bg-white flex-1"
                      value={actForm.activity_date} onChange={e => setActForm(f => ({ ...f, activity_date: e.target.value }))} />
                  </div>
                  <textarea rows={2} placeholder={t('pipeline.activityPlaceholder')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white resize-none"
                    value={actForm.summary} onChange={e => setActForm(f => ({ ...f, summary: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitActivity() }} />
                  <div className="flex justify-end">
                    <button onClick={submitActivity} disabled={actSaving || !actForm.summary.trim()}
                      className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors">
                      {actSaving ? t('common:actions.saving') : t('pipeline.logBtn')}
                    </button>
                  </div>
                </div>
                {selectedLead.activities.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">{t('pipeline.noActivities')}</p>
                ) : (
                  <div className="space-y-3">
                    {selectedLead.activities.map(act => {
                      const typeInfo = ACTIVITY_TYPE_DEFS.find(typ => typ.id === act.type) || { icon: '📋' }
                      return (
                        <div key={act.id} className="flex gap-2.5 group">
                          <span className="text-base shrink-0 mt-0.5 select-none">{typeInfo.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{act.summary}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {fmtDateFull(act.activity_date)}{act.created_by ? ` · ${act.created_by.display_name}` : ''}
                            </p>
                          </div>
                          <button onClick={() => handleDeleteActivity(act.id)}
                            className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5 text-sm">×</button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New lead modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6 max-h-[90vh] overflow-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{t('pipeline.newLeadTitle')}</h2>
            {formError && <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{formError}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.companyNameLabel')}</label>
                <input autoFocus className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') submitNew() }} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.contactNameLabel')}</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.contactEmailLabel')}</label>
                <input type="email" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.dealValueLabel')}</label>
                  <input type="number" min="0" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.currencyLabel')}</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('pipeline.stageLabel')}</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}>
                  {STAGE_DEFS.map(s => <option key={s.id} value={s.id}>{t(`stages.${s.id}`)}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={submitNew} disabled={saving}
                className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors">
                {saving ? t('pipeline.creatingBtn') : t('pipeline.createBtn')}
              </button>
              <button onClick={() => setShowNew(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-400 w-20 shrink-0 text-xs pt-0.5">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  )
}
